import { useCallback, useMemo } from "react";
import {
  GetJobReadinessDashboardQuery,
  GetJobReadinessDashboardQueryVariables,
  useGetJobReadinessDashboardLazyQuery,
} from "generated/graphql";
import {
  emptyJobReadinessDashboard,
  JobReadinessDashboard,
  JobReadinessDailyTotal,
  JobReadinessJobRow,
  JobReadinessMaterialLine,
} from "types/jobReadiness";

const centsToDollars = (value: number | string | null | undefined) => Number(value ?? 0) / 100;

const mapMaterialLine = (line: JobReadinessMaterialLine): JobReadinessMaterialLine => ({
  sourceName: line.sourceName,
  materialName: line.materialName,
  quantity: Number(line.quantity ?? 0),
  unitAbbr: line.unitAbbr,
  trucks: Number(line.trucks ?? 0),
  hasPlant: Boolean(line.hasPlant),
});

const mapJobRow = (row: JobReadinessJobRow): JobReadinessJobRow => ({
  ...row,
  checklistTotal: Number(row.checklistTotal ?? 0),
  checklistOutstanding: Number(row.checklistOutstanding ?? 0),
  revenueScheduled: centsToDollars(row.revenueScheduled),
  profitScheduled: centsToDollars(row.profitScheduled),
  materialLines: (row.materialLines ?? []).map(mapMaterialLine),
});

const mapDailyTotal = (dailyTotal: JobReadinessDailyTotal): JobReadinessDailyTotal => ({
  ...dailyTotal,
  totalQuantity: Number(dailyTotal.totalQuantity ?? 0),
  totalTrucks: Number(dailyTotal.totalTrucks ?? 0),
  materialRows: (dailyTotal.materialRows ?? []).map(mapMaterialLine),
});

export default function useGetJobReadinessDashboard() {
  const [query, { data, loading, error }] = useGetJobReadinessDashboardLazyQuery({
    fetchPolicy: "network-only",
  });
  const getJobReadinessDashboard = useCallback(
    (variables: GetJobReadinessDashboardQueryVariables) => query({ variables }),
    [query]
  );

  const readinessData = useMemo<JobReadinessDashboard>(() => {
    const payload = data?.getJobReadinessDashboard as
      | GetJobReadinessDashboardQuery["getJobReadinessDashboard"]
      | undefined;
    if (!payload) {
      return emptyJobReadinessDashboard;
    }

    return {
      summary: {
        revenueScheduled: centsToDollars(payload.summary?.revenueScheduled),
        profitScheduled: centsToDollars(payload.summary?.profitScheduled),
        jobsScheduled: Number(payload.summary?.jobsScheduled ?? 0),
        jobsConfirmedWithClient: Number(payload.summary?.jobsConfirmedWithClient ?? 0),
        jobsWithOutstandingChecklistItems: Number(payload.summary?.jobsWithOutstandingChecklistItems ?? 0),
        totalQuantity: Number(payload.summary?.totalQuantity ?? 0),
        totalTrucks: Number(payload.summary?.totalTrucks ?? 0),
      },
      exceptions: {
        jobsNotConfirmedWithClient: Number(payload.exceptions?.jobsNotConfirmedWithClient ?? 0),
        jobsMissingPlantAssignment: Number(payload.exceptions?.jobsMissingPlantAssignment ?? 0),
        jobsMissingMaterialQuantity: Number(payload.exceptions?.jobsMissingMaterialQuantity ?? 0),
        materialLinesWithoutTrucks: Number(payload.exceptions?.materialLinesWithoutTrucks ?? 0),
      },
      jobs: (payload.jobs ?? []).map(mapJobRow),
      dailyTotals: (payload.dailyTotals ?? []).map(mapDailyTotal),
    };
  }, [data]);

  return {
    getJobReadinessDashboard,
    readinessData,
    loading,
    error,
  } as const;
}
